import React from "react";

// Page-level loading.
//
// A skeleton rather than a centred spinner: it holds the page's shape so the
// layout doesn't jump when content arrives, and it reads as "nearly there"
// instead of "nothing is happening".
//
// `variant` picks the shape to mirror:
//   list    — header + toolbar + table rows (default)
//   stats   — header + KPI row + table rows
//   form    — header + a form card
//   detail  — header + two-column panels
// `header={false}` when the page already renders its own heading and only the
// content area below it is loading — otherwise the skeleton draws a second one.
export default function LoadingState({ label = "Loading", variant = "list", header = true, className = "" }) {
  return (
    <div className={`space-y-6 ${className}`} role="status" aria-busy="true" aria-label={label}>
      <span className="sr-only">{label}</span>

      {header && (
        <div className="space-y-2">
          <Bar className="h-7 w-56" />
          <Bar className="h-4 w-80" />
        </div>
      )}

      {variant === "stats" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <Bar className="h-3 w-20" delay={i * 70} />
                  <Bar className="h-7 w-16" delay={i * 70} />
                </div>
                <Bar className="h-9 w-9 rounded-xl" delay={i * 70} />
              </div>
            </div>
          ))}
        </div>
      )}

      {variant === "form" && (
        <div className="card max-w-4xl space-y-5">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="space-y-2">
              <Bar className="h-3 w-32" delay={i * 80} />
              <Bar className="h-9 w-full rounded-[10px]" delay={i * 80} />
            </div>
          ))}
        </div>
      )}

      {variant === "detail" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }, (_, col) => (
            <div key={col} className="card space-y-3">
              <Bar className="h-4 w-32" delay={col * 90} />
              {Array.from({ length: 5 }, (_, i) => (
                <Bar key={i} className="h-4 w-full" delay={col * 90 + i * 60} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Rows only — for use inside a card the page already renders. */}
      {variant === "rows" && <Rows />}

      {(variant === "list" || variant === "stats") && (
        <div className="card !p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
            <Bar className="h-9 w-64 rounded-[10px]" />
            <Bar className="h-4 w-28" />
          </div>
          <Rows />
        </div>
      )}
    </div>
  );
}

function Rows({ count = 6 }) {
  return (
    <div className="divide-y divide-gray-100">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-3.5">
          <Bar className="h-4 w-4 rounded" delay={i * 60} />
          <Bar className="h-4 flex-1 max-w-[220px]" delay={i * 60} />
          <Bar className="h-4 w-28 hidden sm:block" delay={i * 60} />
          <Bar className="h-4 w-24 hidden md:block" delay={i * 60} />
          <Bar className="h-5 w-16 rounded-full ml-auto" delay={i * 60} />
        </div>
      ))}
    </div>
  );
}

// One shimmering placeholder. The stagger stops the whole page pulsing in
// lockstep, which reads as a broken flash rather than loading.
function Bar({ className = "", delay = 0 }) {
  return (
    <div
      className={`skeleton-bar rounded-md ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

// Skeleton rows for a table that's already rendered its own <thead>. Returns
// <tr> elements, so it must be used inside a <tbody>.
export function TableRowsSkeleton({ columns = 4, rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <td key={c} className="px-5 py-3">
              <Bar className={`h-4 ${c === 0 ? 'w-32' : 'w-24'}`} delay={r * 60} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Small spinner for inside buttons and table cells, where a skeleton would
// blow the layout apart. `light` for use on a dark or filled button.
export function InlineSpinner({ light = false, className = "" }) {
  return (
    <span
      className={`inline-block h-4 w-4 rounded-full border-2 animate-spin align-[-2px] ${
        light ? "border-white/30 border-t-white" : "border-gray-200 border-t-cloudza-600"
      } ${className}`}
      aria-hidden="true"
    />
  );
}
